import { z } from 'zod';
import { BallColorSchema, RedCountSchema } from "./balls.js";
/**
 * Who produced the event.
 * `manual` always wins over `auto`: a human correction must never be
 * silently overwritten by the recogniser.
 */
export const EventSourceSchema = z.enum(['manual', 'auto', 'system']);
/** Player seat. Snooker is always head-to-head. */
export const PlayerIndexSchema = z.union([z.literal(0), z.literal(1)]);
/**
 * Scoring mode. The scoreboard is the control plane: the operator decides
 * how much authority the recogniser has.
 *  - manual:    AI off entirely, humans enter every shot
 *  - assisted:  AI proposes, a human confirms before it hits the score
 *  - auto:      AI events apply immediately, humans correct afterwards
 */
export const ScoringModeSchema = z.enum(['manual', 'assisted', 'auto']);
const base = {
    /** Monotonic sequence number within a match. Assigned by the server. */
    seq: z.number().int().nonnegative(),
    matchId: z.string().min(1),
    at: z.string().datetime(),
    source: EventSourceSchema,
    /** Idempotency key so a retried client request is not applied twice. */
    clientId: z.string().min(1).optional(),
    /** Server-captured live-broadcast evidence. Missing legacy evidence is unranked. */
    rankingLive: z.boolean().optional(),
};
/**
 * A start of points given away, so two players of different standards can
 * play a frame that is actually in doubt.
 *
 * One player gives and the other receives: `from` is whoever gives the points
 * away, and their opponent begins *every* frame that many points ahead. Per
 * frame rather than per match, because a handicap that only applied to the
 * first frame would be worth less and less as a match went on, and because
 * this is how a club actually plays it.
 *
 * The floor is one point — nought is not a handicap, it is what turning the
 * switch off already means — and the ceiling is 147, one maximum break.
 * Further than that and the receiver has won before a ball is struck.
 *
 * It rides on `match.started` rather than being a column somewhere, so it
 * replays with everything else: a board that reloads mid-match rebuilds the
 * same scores, and a frame that starts an hour later starts correctly.
 */
export const MIN_HANDICAP = 1;
export const MAX_HANDICAP = 147;
export const HandicapSchema = z.object({
    /** The stronger player: the one giving points away. */
    from: PlayerIndexSchema,
    points: z.number().int().min(MIN_HANDICAP).max(MAX_HANDICAP),
});
export const MatchStartedSchema = z.object({
    ...base,
    type: z.literal('match.started'),
    practice: z.literal(true).optional(),
    players: z.tuple([z.string().min(1), z.string().min(1)]),
    /** Optional ISO country codes for each seat, for OSD flag rendering. */
    countries: z
        .tuple([z.string().min(2).max(10).nullable(), z.string().min(2).max(10).nullable()])
        .default([null, null]),
    bestOf: z.number().int().positive().default(1),
    /** Omitted in legacy logs, which always use 15 reds. */
    redCount: RedCountSchema.optional(),
    /** Minutes the match may run for, or `null` for no limit. */
    timeLimitMinutes: z.number().int().positive().nullable().default(null),
    /** Points one player spots the other at the start of every frame. */
    handicap: HandicapSchema.nullable().default(null),
});
export const FrameStartedSchema = z.object({
    ...base,
    type: z.literal('frame.started'),
    frameNumber: z.number().int().positive(),
    /** Who breaks off. */
    startingPlayer: PlayerIndexSchema,
});
/**
 * A legal pot. Per the frontend/backend contract a single event carries only
 * one colour group: either one or more reds, or exactly one non-red colour.
 */
export const ShotPotSchema = z
    .object({
    ...base,
    type: z.literal('shot.pot'),
    player: PlayerIndexSchema,
    color: BallColorSchema,
    count: z.number().int().positive().default(1),
    /**
     * This pot was a nominated free ball.
     *
     * `color` still names the ball that physically went down, because that is
     * what an operator tapped and what a break log has to show. What it is
     * *worth* is not that ball's own value: a free ball scores the value of
     * the ball on, so the two have to be recorded separately or the log
     * cannot be replayed into the right score.
     */
    freeBall: z.boolean().default(false),
})
    .refine((e) => e.color === 'red' || e.count === 1, {
    message: 'only reds may be potted more than once in a single shot',
    path: ['count'],
})
    .refine((e) => !e.freeBall || e.count === 1, {
    message: 'a free ball is a single nominated ball',
    path: ['count'],
});
/**
 * Just the ball and how many of it, for checking a pot before it is stored.
 *
 * `ShotPotSchema` carries the envelope the store adds — `seq`, `matchId`,
 * `at` — which a request body does not have yet, and its `.refine()` means it
 * cannot be narrowed with `.omit()`. This is the same shape question asked of
 * the part a caller actually sends.
 */
export const PotIntentSchema = z
    .object({
    color: BallColorSchema,
    count: z.number().int().positive().default(1),
    freeBall: z.boolean().default(false),
})
    .refine((e) => e.color === 'red' || e.count === 1, {
    message: 'only reds may be potted more than once in a single shot',
    path: ['count'],
})
    .refine((e) => !e.freeBall || e.count === 1, {
    message: 'a free ball is a single nominated ball',
    path: ['count'],
});
/** A shot that scored nothing and handed the table over. */
export const ShotMissSchema = z.object({
    ...base,
    type: z.literal('shot.miss'),
    player: PlayerIndexSchema,
});
/**
 * A foul. Points go to the opponent. Minimum 4 in snooker.
 * Fouls are always entered by a human: at 10 fps the recogniser cannot see
 * which ball the cue ball touched first.
 */
export const ShotFoulSchema = z.object({
    ...base,
    type: z.literal('shot.foul'),
    player: PlayerIndexSchema,
    points: z.number().int().min(4).max(7),
    /** Set after a "foul and a miss" call, when the offender must play again. */
    offenderPlaysAgain: z.boolean().default(false),
    restoration: z.object({
        shotId: z.string().min(1),
        baselineSeq: z.number().int().nonnegative(),
        replacedSeqs: z.array(z.number().int().positive()).max(64),
    }).optional(),
    freeBall: z.boolean().default(false),
});
/**
 * The referee declares a free ball to the player now at the table.
 *
 * Separate from the foul that caused it, rather than only a flag on it,
 * because the two are decided at different moments by different people. The
 * foul is called as the shot ends; whether the incoming player is snookered
 * on every ball on is a question about the table *after* the balls have come
 * to rest, and at a club it is asked out loud by the player and answered by
 * whoever is refereeing. A scoreboard that could only record a free ball at
 * the instant the foul was entered would be asking for the answer before the
 * question has been put.
 *
 * `awarded: false` withdraws it, because a button that can be pressed by
 * mistake needs a way back that is not Undo — Undo would take the foul with
 * it.
 */
export const FreeBallAwardedSchema = z.object({
    ...base,
    type: z.literal('freeball.awarded'),
    /** The striker who has the free ball: the player *not* at fault. */
    player: PlayerIndexSchema,
    awarded: z.boolean().default(true),
});
/** Reds removed from the table without being scored (e.g. potted in a foul). */
export const RedsRemovedSchema = z.object({
    ...base,
    type: z.literal('reds.removed'),
    count: z.number().int().positive(),
});
export const RedAdjustmentDeltaSchema = z.union([z.literal(-1), z.literal(1)]);
/** Manual table correction; changes neither scored points nor the match format. */
export const RedsAdjustedSchema = z.object({
    ...base,
    type: z.literal('reds.adjusted'),
    source: z.literal('manual'),
    delta: RedAdjustmentDeltaSchema,
});
/** Absolute score override. The escape hatch when replay cannot express reality. */
export const ScoreCorrectedSchema = z.object({
    ...base,
    type: z.literal('score.corrected'),
    player: PlayerIndexSchema,
    score: z.number().int().nonnegative(),
    reason: z.string().max(200).optional(),
});
export const MAX_SCORE_ADDITION = 147;
export const ScoreAdditionPointsSchema = z.number().int().min(1).max(MAX_SCORE_ADDITION);
export const ScoreAddedSchema = z.object({
    ...base,
    type: z.literal('score.added'),
    source: z.literal('manual'),
    player: PlayerIndexSchema,
    points: ScoreAdditionPointsSchema,
    // Older entries leave the visit open when replayed.
    endTurn: z.boolean().optional(),
});
/** Hand the table over without recording a shot. */
export const TurnSwitchedSchema = z.object({
    ...base,
    type: z.literal('turn.switched'),
    player: PlayerIndexSchema,
});
/**
 * Soft-retract an earlier event. Undo never deletes history: it appends.
 * This keeps the audit trail intact and makes replay deterministic.
 */
export const EventRetractedSchema = z.object({
    ...base,
    type: z.literal('event.retracted'),
    targetSeq: z.number().int().nonnegative(),
});
export const ModeChangedSchema = z.object({
    ...base,
    type: z.literal('mode.changed'),
    mode: ScoringModeSchema,
});
export const StreamingChangedSchema = z.object({
    ...base,
    type: z.literal('streaming.changed'),
    enabled: z.boolean(),
});
/**
 * The table is paused for a referee decision or a physical table action.
 *
 * This is operational state, not scoring: while paused the ball pad is locked
 * and the UI shows a prominent reason, but no points move.
 */
export const PauseReasonSchema = z.enum([
    'waiting_for_respot',
    'foul_and_miss',
    'manual_review',
    'other',
]);
export const TablePausedSchema = z.object({
    ...base,
    type: z.literal('table.paused'),
    reason: PauseReasonSchema.default('manual_review'),
    resetOverlay: z.boolean().default(false),
    message: z.string().max(200).optional(),
});
/** Resume play after a `table.paused` period. */
export const TableResumedSchema = z.object({
    ...base,
    type: z.literal('table.resumed'),
});
export const FrameEndedSchema = z.object({
    ...base,
    type: z.literal('frame.ended'),
    winner: PlayerIndexSchema,
});
export const MatchEndedSchema = z.object({
    ...base,
    type: z.literal('match.ended'),
    reason: z.literal('inactivity').optional(),
    winner: PlayerIndexSchema.optional(),
});
export const MatchResumedSchema = z.object({
    ...base,
    type: z.literal('match.resumed'),
    closedAt: z.string().datetime(),
});
export const MatchEventSchema = z.discriminatedUnion('type', [
    MatchStartedSchema,
    FrameStartedSchema,
    ShotMissSchema,
    ShotFoulSchema,
    FreeBallAwardedSchema,
    RedsRemovedSchema,
    RedsAdjustedSchema,
    ScoreCorrectedSchema,
    ScoreAddedSchema,
    TurnSwitchedSchema,
    EventRetractedSchema,
    ModeChangedSchema,
    StreamingChangedSchema,
    TablePausedSchema,
    TableResumedSchema,
    FrameEndedSchema,
    MatchEndedSchema,
    MatchResumedSchema,
]);
/**
 * `shot.pot` carries a `.refine()` so it cannot sit inside a
 * discriminatedUnion. Parse against the union first, fall back to the pot
 * schema.
 */
export function parseMatchEvent(input) {
    const asPot = ShotPotSchema.safeParse(input);
    if (asPot.success)
        return asPot.data;
    return MatchEventSchema.parse(input);
}
//# sourceMappingURL=events.js.map